import { useEffect, useState } from "react";
import type { EnvironmentId, RuntimeDiagnosticsResponse } from "@cmmd-forge/contracts";

import { buildPrimaryFetchInit } from "../../environments/primary";
import { resolveEnvironmentHttpUrl } from "../../environments/runtime/catalog";
import {
  CLOUD_MACHINE_INVENTORY_PATH,
  fetchCloudMachineInventoryJson,
} from "./cloudEnvironmentInventory.logic";

export interface CloudEnvironmentInventory {
  readonly inventory: RuntimeDiagnosticsResponse | undefined;
  readonly error: string | undefined;
  readonly pending: boolean;
  readonly probed: boolean;
  readonly refresh: () => void;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function fetchCloudMachineInventory(
  environmentId: EnvironmentId,
): Promise<RuntimeDiagnosticsResponse> {
  return (await fetchCloudMachineInventoryJson(
    resolveEnvironmentHttpUrl({
      environmentId,
      pathname: CLOUD_MACHINE_INVENTORY_PATH,
    }),
    buildPrimaryFetchInit({ credentials: "include" }),
  )) as RuntimeDiagnosticsResponse;
}

/**
 * Machine inventory for one Cloud Environment. Recheck only. Selection does
 * not start a fetch.
 */
export function useCloudEnvironmentInventory(
  environmentId: EnvironmentId | null,
  enabled: boolean,
): CloudEnvironmentInventory {
  const [state, setState] = useState<{
    readonly inventory: RuntimeDiagnosticsResponse | undefined;
    readonly error: string | undefined;
    readonly pending: boolean;
  }>({ inventory: undefined, error: undefined, pending: false });
  const [reloadToken, setReloadToken] = useState(0);
  const [probedEnvironmentId, setProbedEnvironmentId] = useState<EnvironmentId | null>(null);

  useEffect(() => {
    if (!enabled || environmentId === null || probedEnvironmentId !== environmentId) {
      setState({ inventory: undefined, error: undefined, pending: false });
      return;
    }
    let cancelled = false;
    setState((previous) => ({ ...previous, pending: true }));
    void fetchCloudMachineInventory(environmentId)
      .then((inventory) => {
        if (cancelled) return;
        setState({ inventory, error: undefined, pending: false });
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setState({
          inventory: undefined,
          error: errorMessage(cause),
          pending: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, environmentId, probedEnvironmentId, reloadToken]);

  function refresh(): void {
    if (!enabled) return;
    setProbedEnvironmentId(environmentId);
    setReloadToken((token) => token + 1);
  }

  return {
    ...state,
    probed: probedEnvironmentId === environmentId && environmentId !== null,
    refresh,
  };
}
